import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import axios from "axios";
import type { Product } from "@/lib/types";
import { useViewerId } from "@/hooks/useViewerId";
import { PremiumProductCard } from "@/components/shop/ProductCards";

const API = process.env.REACT_APP_BACKEND_URL as string;
const shopApi = axios.create({ baseURL: `${API}/api/shop` });

// ------------------------ Featured Collections ------------------------
// Fetches admin-curated shelves from /api/shop/featured-shelves. Each shelf
// runs its own /products query with the admin-defined filter recipe.
const ACCENT_CLS: Record<string, string> = {
  saffron: "text-[color:var(--sb-saffron)]",
  emerald: "text-emerald-600 dark:text-emerald-400",
  rose:    "text-rose-500",
  navy:    "text-slate-900 dark:text-white",
};

export interface ShelfConfig {
  id: string;
  title: string;
  subtitle: string;
  accent: string;
  params: Record<string, any>;
  enabled?: boolean;
}

const FeaturedShelf: React.FC<{ shelf: ShelfConfig }> = ({ shelf }) => {
  const q = useQuery({
    queryKey: ["shop-shelf", shelf.id, shelf.params],
    queryFn: async () => (await shopApi.get("/products", { params: shelf.params })).data,
    staleTime: 5 * 60_000,
  });
  const items: Product[] = q.data?.items || [];
  if (q.isSuccess && items.length < 3) return null;
  const filterHref = `/shop?${new URLSearchParams(
    Object.fromEntries(Object.entries(shelf.params).map(([k, v]) => [k, String(v)])),
  ).toString()}`;
  const accent = ACCENT_CLS[shelf.accent] || ACCENT_CLS.saffron;
  return (
    <section className="mb-6" data-testid={`shop-shelf-${shelf.id}`}>
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-widest font-display font-bold ${accent}`}>Featured shelf</div>
          <h3 className="font-serif-display font-bold text-lg sm:text-xl text-slate-900 dark:text-white tracking-tight mt-0.5">
            {shelf.title}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-lg mt-0.5">{shelf.subtitle}</p>
        </div>
        <Link
          to={filterHref}
          data-testid={`shop-shelf-${shelf.id}-view-all`}
          className="shrink-0 inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-orange-500 font-display font-semibold"
        >
          View all <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
      {q.isLoading ? (
        <div className="grid grid-flow-col auto-cols-[minmax(180px,1fr)] gap-3 overflow-x-auto pb-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-slate-100 dark:bg-slate-800 animate-pulse border border-slate-200 dark:border-slate-800 aspect-[3/4]" />
          ))}
        </div>
      ) : (
        <div className="grid grid-flow-col auto-cols-[minmax(220px,1fr)] gap-3 overflow-x-auto pb-3 sg-scrollbar-thin">
          {items.slice(0, shelf.params?.limit || 8).map((p, i) => (
            <PremiumProductCard key={p.product_id} p={p} listName={`shelf-${shelf.id}`} index={i} />
          ))}
        </div>
      )}
    </section>
  );
};

export const FeaturedCollections: React.FC = () => {
  const q = useQuery<{ shelves: ShelfConfig[] }>({
    queryKey: ["public-featured-shelves"],
    queryFn: async () => (await shopApi.get("/featured-shelves")).data,
    staleTime: 5 * 60_000,
  });
  const shelves = q.data?.shelves || [];
  return (
    <div className="mb-8" data-testid="shop-featured-collections">
      <RecentlyViewedShelf />
      {shelves.map((s) => <FeaturedShelf key={s.id} shelf={s} />)}
    </div>
  );
};

// Personalised "You just viewed" shelf — reads the caller's own PDP visit
// history via the anon+auth viewer id. Hidden entirely when the visitor
// has no history (first-time landing on /shop before browsing anything).
const RecentlyViewedShelf: React.FC = () => {
  const viewer = useViewerId();
  const q = useQuery<{ items: Product[]; total: number }>({
    queryKey: ["shop-recently-viewed", viewer],
    queryFn: async () => (await shopApi.get("/recently-viewed", { params: { viewer, limit: 8 } })).data,
    enabled: !!viewer,
    staleTime: 60_000,
  });
  const items = q.data?.items || [];
  if (items.length < 2) return null;
  return (
    <section className="mb-6" data-testid="shop-shelf-recently-viewed">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 dark:text-slate-400">Just for you</div>
          <h3 className="font-serif-display font-bold text-lg sm:text-xl text-slate-900 dark:text-white tracking-tight mt-0.5">
            You just viewed
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-lg mt-0.5">
            A quick jump back to what you were checking out.
          </p>
        </div>
      </div>
      <div className="grid grid-flow-col auto-cols-[minmax(220px,1fr)] gap-3 overflow-x-auto pb-3 sg-scrollbar-thin">
        {items.map((p, i) => (
          <PremiumProductCard key={p.product_id} p={p} listName="recently-viewed" index={i} />
        ))}
      </div>
    </section>
  );
};
